<script>
import FileBrowser from '@/components/file-browser/FileBrowser';
import Panel from '@/components/ui/Panel';
import colorString from 'color-string';
import { getType } from '@/modules/editor/region-types.js';
import { dirname, join, removeBasePath } from '@/modules/file-utilities';
import URI from 'urijs';

export default {
  name: 'BackgroundPanel',
  components: {
    FileBrowser,
    Panel
  },
  props: {
    page: {
      type: Object,
      required: true
    },
    site: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      backgroundColor: '',
      backgroundImage: '',
      backgroundRepeat: '',
      backgroundSize: '',
      element: null,
      isFileBrowserOpen: false,
      repeatOptions: ['no-repeat', 'repeat'],
      sizeOptions: ['contain', 'cover', '100%', '75%', '50%', '25%']
    };
  },
  methods: {
    show(options) {
      const element = options.element;
      const backgroundImage = element.style.backgroundImage;
      const backgroundColor = element.style.backgroundColor;
      const backgroundColorAsRgb = colorString.get.rgb(backgroundColor);
      const backgroundSize = element.style.backgroundSize;
      const backgroundRepeat = element.style.backgroundRepeat;
      this.element = element;
      this.backgroundImage = this.parseCssUrl(backgroundImage);
      this.backgroundColor = backgroundColorAsRgb ? colorString.to.rgb(backgroundColorAsRgb) : '';
      this.backgroundSize = this.sizeOptions.includes(backgroundSize) ? backgroundSize : 'cover';
      this.backgroundRepeat = this.repeatOptions.includes(backgroundRepeat) ? backgroundRepeat : 'no-repeat';
      this.$refs.panel.show();
    },

    hide() {
      this.$refs.panel.hide();
    },

    handleFileBrowserSelect(type, files) {
      if (type === 'upload') {
        const file = files[0];
        const pageUrl = new URI(this.site.url).path(URI.joinPaths(this.site.url, this.page.path));
        const fileUrl = new URI(this.site.url).path(
          URI.joinPaths(this.site.url, removeBasePath(file.path, this.site.homeFolder))
        );
        const backgroundImage = fileUrl.relativeTo(pageUrl).toString();

        this.backgroundImage = backgroundImage;
        this.$refs.fileBrowserPopover.doClose();
      } else if (type === 'unsplash') {
        this.backgroundImage = files[0].urls.regular;
        this.$refs.fileBrowserPopover.doClose();
      }
    },

    handleHide() {
      const type = getType(this.element);
      if (type === 'block' || type === 'inline') {
        this.element.focus();
      }

      this.$refs.colorPicker.hide();
    },

    handleShow() {
      this.$refs.backgroundImage.focus();
    },

    handleSubmit() {
      const element = this.element;
      this.$refs.panel.hide();
      element.style.backgroundImage = this.backgroundImage ? this.createCssUrl(this.backgroundImage) : null;
      element.style.backgroundColor = this.backgroundColor;
      element.style.backgroundRepeat = this.backgroundRepeat;
      element.style.backgroundSize = this.backgroundSize;
    },

    createCssUrl(url) {
      return 'url("' + url + '")';
    },

    hideFileBrowser() {
      this.$refs.fileBrowser.close();
      this.isFileBrowserOpen = false;
    },

    showFileBrowser() {
      let path = this.site.uploadFolder;

      // If the current src looks like a relative path, use that directory
      if (!this.backgroundImage.match(/^https?:\/\//i)) {
        const fileUrl = new URI(this.backgroundImage);
        path = join(this.site.homeFolder, dirname(fileUrl.path()));
      }

      this.isFileBrowserOpen = true;
      this.$refs.fileBrowser.open({ path });
    },

    parseCssUrl(url) {
      url = url.match(/^url\((['"]?)(.*)\1\)$/);
      url = url ? decodeURI(url[2]) : '';
      return url;
    }
  }
};
</script>

<template>
  <panel ref="panel" :title="$t('Background')" @show="handleShow()" @hide="handleHide()">
    <el-form label-position="top" novalidate @submit.native.prevent="handleSubmit">
      <fieldset>
        <!-- Image -->
        <el-form-item :label="$t('Image')">
          <el-input
            ref="backgroundImage"
            v-model="backgroundImage"
            type="url"
            prefix-icon="el-icon-fa-image"
            clearable
            auto-complete="off"
          >
            <template slot="append">
              <el-popover
                ref="fileBrowserPopover"
                placement="bottom"
                trigger="click"
                @show="showFileBrowser()"
                @hide="hideFileBrowser()"
              >
                <el-button slot="reference">
                  {{ $t('Browse') }}
                </el-button>
                <file-browser
                  ref="fileBrowser"
                  :base-path="site.uploadFolder"
                  :multiple="false"
                  :show-unsplash="true"
                  :site="site"
                  type="popover"
                  @select="handleFileBrowserSelect"
                />
              </el-popover>
            </template>
          </el-input>
        </el-form-item>

        <!-- Size -->
        <el-form-item :label="$t('Size')">
          <el-select v-model="backgroundSize">
            <el-option :label="$t('Cover')" value="cover" />
            <el-option :label="$t('Best Fit')" value="contain" />
            <el-option :label="$t('100%')" value="100%" />
            <el-option :label="$t('75%')" value="75%" />
            <el-option :label="$t('50%')" value="50%" />
            <el-option :label="$t('25%')" value="25%" />
          </el-select>
        </el-form-item>

        <!-- Repeat -->
        <el-form-item :label="$t('Repeat')">
          <el-radio-group v-model="backgroundRepeat">
            <el-radio-button label="no-repeat">
              {{ $t('None') }}
            </el-radio-button>
            <el-radio-button label="repeat">
              {{ $t('Tile') }}
            </el-radio-button>
          </el-radio-group>
        </el-form-item>

        <!-- Color -->
        <el-form-item :label="$t('Color')">
          <el-color-picker ref="colorPicker" v-model="backgroundColor" show-alpha class="color-picker" />
        </el-form-item>

        <el-button type="primary" native-type="submit">
          {{ $t('Update Background') }}
        </el-button>
      </fieldset>
    </el-form>
  </panel>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

//
// No styles yet
//
</style>
